import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';

import Headline from '../headline/Headline';
import AdvantageCard, { AdvantageTranslationObject } from './AdvantageCard';

const TrustpilotWidget = dynamic(() => import('@components/trustpilot-widget/TrustpilotWidget'), { ssr: false });

interface Props {
  advantages?: (string | { key: string; values: Record<string, unknown> })[];
  maxCols?: number;
  isEmployer?: boolean;
  titleTranslationKey: string;
  subTitleTranslationKey?: string;
  altTextShown?: boolean;
  namespace?: string;
  highlightTrustpilot?: boolean;
  TitleComponent?: keyof React.JSX.IntrinsicElements;
  SubTitleComponent?: keyof React.JSX.IntrinsicElements;
  hideTrustpilot?: boolean;
}

const Advantages: React.FC<Props> = ({
  advantages: propsAdvantages,
  maxCols = MAX_COLS,
  titleTranslationKey,
  subTitleTranslationKey = 'yourAdvantages',
  altTextShown = false,
  namespace = 'landingPage',
  TitleComponent = 'h2',
  SubTitleComponent = 'h3',
  hideTrustpilot = false,
}) => {
  const { t } = useTranslation(namespace, { keyPrefix: 'advantages' });
  const loadedAdvantages = t('list', { returnObjects: true }) as AdvantageTranslationObject[];

  return (
    <div>
      <div className='flex flex-col'>
        <TitleComponent className='text-default mb-6 text-center text-base md:text-2xl'>{t(titleTranslationKey)}</TitleComponent>
        <Headline i18nKey={`${namespace}:advantages.${subTitleTranslationKey}`} Component={SubTitleComponent} />
      </div>
      <div className='flex flex-col items-center gap-12 md:items-end'>
        <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-${maxCols}`}>
          {Array.isArray(loadedAdvantages)
            ? loadedAdvantages.map(advantage => <AdvantageCard key={advantage.title} isLoadedTranslation advantage={advantage} />)
            : propsAdvantages?.map(advantage => {
                const key = typeof advantage === 'string' ? advantage : advantage.key;
                return (
                  <AdvantageCard
                    altTextShown={altTextShown}
                    key={key}
                    name={key}
                    namespace={namespace}
                    translationValues={typeof advantage === 'string' ? undefined : advantage.values}
                  />
                );
              })}
        </div>
      </div>
      {!hideTrustpilot && (
        <div className='mt-12 flex flex-col items-center md:items-end'>
          <TrustpilotWidget />
        </div>
      )}
    </div>
  );
};

const MAX_COLS = 4;

export default Advantages;
